import React, { useEffect, useMemo, useRef } from "react";
import { InfiniteData } from "@tanstack/react-query";
import clsx from "clsx";
import { useTranslation } from "react-i18next";
import { HiOutlineDocumentMagnifyingGlass } from "react-icons/hi2";

import { InfinitePaginatedPromise, PaginatedData } from "@/types/InfiniteQuery.type";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import useInfiniteScroll from "@/hooks/useInfiniteScroll";
import Input from "@/components/atoms/input/Input";
import InputLabel from "@/components/atoms/input/InputLabel";
import Spinner from "@/components/atoms/Spinner";

export type InfiniteComboBoxProp<T extends { id: string; name: string }> = {
  label: string;
  options: InfiniteData<PaginatedData<T>, unknown> | undefined;
  displayValue?: (option: T) => string;
  placeholder?: string;
  renderOption?: (option: T) => React.ReactNode;
  loadingNode?: React.ReactNode;
  hasError?: boolean;
  leftPlaceholder?: React.ReactNode | string;
  optionsClassName?: string;
  disabled?: boolean;
  isLoading?: boolean;
  emptyNode?: React.ReactNode;
  isFetchingNextPage: boolean;
  fetchNextPage: () => InfinitePaginatedPromise<T>;
  hasNextPage: boolean;
  onQueryChange: (query: string) => void;
};

const InfiniteComboBox = <T extends { id: string; name: string }>({
  label,
  options,
  displayValue = (option) => option?.name,
  placeholder,
  renderOption,
  loadingNode,
  hasError,
  leftPlaceholder,
  optionsClassName,
  disabled,
  isLoading = false,
  emptyNode,
  isFetchingNextPage = false,
  fetchNextPage,
  hasNextPage,
  onQueryChange,
}: InfiniteComboBoxProp<T>) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  const { query, setDebounceQuery, isTyping } = useDebounceQuery(400);

  // Flatten the pages into a single array of options
  const flattenedOptions = useMemo(() => {
    if (!options) return [];
    return options.pages.flatMap((page) => page.data);
  }, [options]);

  const observerRef = useInfiniteScroll({
    hasNextPage,
    fetchNextPage,
    containerRef,
  });

  useEffect(() => {
    onQueryChange(query);
  }, [query, onQueryChange]);

  return (
    <div>
      <InputLabel label={label} className={"mb-1.5"} />
      <Input
        hasError={hasError}
        leftPlaceholder={leftPlaceholder}
        onChange={(e) => setDebounceQuery(e.target.value)}
        placeholder={placeholder || t("select.options")}
        disabled={disabled}
      />

      <div
        ref={containerRef}
        className={clsx("mt-4 h-full max-h-80 w-full overflow-auto text-sm focus:outline-none", optionsClassName)}
      >
        {isLoading || isTyping
          ? loadingNode || <IsLoading />
          : flattenedOptions.length > 0
            ? flattenedOptions.map((option) =>
                renderOption ? renderOption(option) : <span key={option.id}>{displayValue(option)}</span>,
              )
            : query
              ? emptyNode || <NoResults />
              : null}

        {isFetchingNextPage && (loadingNode || <IsLoading />)}

        {/* Infinite Scroll Trigger */}
        <div ref={observerRef} className={"h-1 min-h-1"} />
      </div>
    </div>
  );
};

export default InfiniteComboBox;

const NoResults = () => {
  const { t } = useTranslation();
  return (
    <div className={"flex flex-col items-center justify-center px-3 py-4 text-center text-sm text-quaternary"}>
      <HiOutlineDocumentMagnifyingGlass className={"mb-2 size-8"} />
      <p>{t("noResults.found")}</p>
      <p>{t("noResults.try")}</p>
    </div>
  );
};

const IsLoading = () => {
  const { t } = useTranslation();
  return (
    <div className={"flex items-center gap-2 px-3 py-4 text-sm text-quaternary"}>
      <Spinner />
      <p>{t("searching")}</p>
    </div>
  );
};
